// Existing JSON remains supported while a new representation is being warmed.
function* forecastPayloadSteps(payload) {
    if (!payload || !Object.prototype.hasOwnProperty.call(payload, 'item_encoding')) return payload;
    const encoding = payload.item_encoding;
    if (encoding?.format !== 'ws-forecast-columns-v1'
        || !Array.isArray(encoding.columns) || encoding.columns.length > 64
        || !Array.isArray(encoding.dictionaries) || encoding.dictionaries.length !== encoding.columns.length) {
        throw new Error('Formato previsionale non valido');
    }
    const {columns, dictionaries} = encoding;
    if (new Set(columns).size !== columns.length || columns.some(key =>
        typeof key !== 'string' || !/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(key)
        || key === 'constructor' || key === 'prototype')) throw new Error('Colonne previsionali non valide');
    if (dictionaries.some(values => values !== null && (!Array.isArray(values)
        || values.some(value => value === null || value === ''
            || !['string', 'number', 'boolean'].includes(typeof value)
            || typeof value === 'number' && !Number.isFinite(value))))) throw new Error('Dizionario previsionale non valido');
    // Rows with the same missing fields share an ordinary object layout.
    // The bounded cache is local to one decode; no forecast data is retained.
    const layouts = new Map();
    const columnCount = columns.length;
    const rowValues = new Array(columnCount);
    let previousLayout, previousLower, previousUpper;
    function layoutFor(values) {
        let lower = 0, upper = 0;
        for (let i = 0; i < columnCount; i++) {
            const value = values[i];
            rowValues[i] = null;
            if (value !== null) {
                const dictionary = dictionaries[i];
                if (dictionary !== null) {
                    if (!Number.isInteger(value) || value < 0 || value >= dictionary.length) throw new Error('Indice previsionale non valido');
                    rowValues[i] = dictionary[value];
                } else {
                    if (value === '' || !['string', 'number', 'boolean'].includes(typeof value)
                        || typeof value === 'number' && !Number.isFinite(value)) throw new Error('Valore previsionale non valido');
                    rowValues[i] = value;
                }
                if (i < 32) lower |= 1 << i;
                else upper |= 1 << (i - 32);
            }
        }
        if (previousLayout && previousLower === lower && previousUpper === upper) return previousLayout;
        const key = `${lower}:${upper}`;
        let layout = layouts.get(key);
        if (!layout) {
            const indices = [];
            for (let i = 0; i < columnCount; i++) if (rowValues[i] !== null) indices.push(i);
            layout = {indices, template: Object.fromEntries(indices.map(i => [columns[i], undefined]))};
            if (layouts.size < 64) layouts.set(key, layout);
        }
        previousLower = lower;
        previousUpper = upper;
        previousLayout = layout;
        return layout;
    }
    let processed = 0;
    function* decodeRows(rows) {
        if (!Array.isArray(rows)) throw new Error('Righe previsionali non valide');
        const decoded = [];
        for (const values of rows) {
            if (values === null) { decoded.push([]); continue; }
            if (!Array.isArray(values) || values.length !== columns.length) throw new Error('Riga previsionale non valida');
            const layout = layoutFor(values);
            const item = {...layout.template};
            for (const i of layout.indices) item[columns[i]] = rowValues[i];
            decoded.push(item);
            if (++processed % 512 === 0) yield;
        }
        return decoded;
    }
    const result = {...payload};
    delete result.item_encoding;
    for (const group of ['items_by_step', 'items_by_day']) {
        if (!payload[group] || typeof payload[group] !== 'object') throw new Error('Gruppo previsionale non valido');
        const decoded = Array.isArray(payload[group]) ? [] : {};
        for (const [key, rows] of Object.entries(payload[group])) {
            Object.defineProperty(decoded, key, {value: yield* decodeRows(rows), enumerable: true, writable: true, configurable: true});
        }
        result[group] = decoded;
    }
    return result;
}

export function decodeForecastPayload(payload) {
    const steps = forecastPayloadSteps(payload);
    let step;
    do { step = steps.next(); } while (!step.done);
    return step.value;
}

function yieldToBrowser() {
    if (typeof globalThis.scheduler?.yield === 'function') return globalThis.scheduler.yield();
    if (typeof MessageChannel === 'undefined') return new Promise(resolve => setTimeout(resolve, 0));
    return new Promise(resolve => {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => { channel.port1.close(); channel.port2.close(); resolve(); };
        channel.port2.postMessage(0);
    });
}

export async function decodeForecastPayloadAsync(payload, yieldTask = yieldToBrowser) {
    const steps = forecastPayloadSteps(payload);
    for (let step = steps.next(); ; step = steps.next()) {
        if (step.done) return step.value;
        await yieldTask();
    }
}

export async function fetchForecastPayload(options = {}) {
    let failure;
    // A separate path prevents a CDN that normalizes query strings from mixing
    // the new representation with the existing external JSON contract.
    for (const url of ['/api/forecast-timeline-columns-v1.php', '/api/forecast-map.php?mode=timeline']) {
        try {
            const response = await fetch(url, {...options, headers: {Accept: 'application/json'}});
            if (!response.ok) throw new Error('Previsioni non disponibili');
            const payload = await decodeForecastPayloadAsync(await response.json());
            if (!payload?.success || !Array.isArray(payload.available_steps) || !payload.items_by_step) throw new Error('Previsioni non valide');
            return payload;
        } catch (error) { failure = error; }
    }
    throw failure;
}
